import { DEFAULT_CURRENCY } from '@/constants/currencies'
import { parseCalendarDate } from '@lib/datetime'
import { placeTextProblem } from '@lib/place'
import { DraftPlace } from './place'

/**
 * Pure, immutable state for the create-trip flow: the ordered destination list
 * plus the user's choices. Every operation returns a new draft so React state
 * updates stay referentially honest; every derived value is a plain function so
 * the flow's rules are unit-testable without a DOM. Mirrors the iOS flow's
 * `CreateTripDraft` so the clients agree on semantics.
 */

/**
 * Wire-contract limits, mirrored from `@orbit/api-types`
 * (`packages/api-types/src/trip/tripDestinations.ts`). That package is not
 * browser-safe at runtime, so the values live here and are pinned against the
 * schema by `__tests__/contract.test.ts`.
 */
export const MAX_DESTINATIONS_PER_TRIP = 40
export const MAX_DESTINATION_DAY_COUNT = 365
export const MAX_TRIP_DAY_COUNT = 730

/** What a new row starts at — a visible default the user adjusts, never a blank. */
export const DEFAULT_DESTINATION_DAYS = 7

export interface DraftDestination {
	/**
	 * Client-side identity for the row, unique within the session. Repeats of
	 * a place are allowed and rows reorder, so neither the place nor the index
	 * can key the list (React keys, drag-and-drop) without the row losing
	 * itself mid-move. Never sent to the server.
	 */
	key: string
	/**
	 * Server destination id when the draft edits an existing trip — the edit
	 * contract keeps an id row's days. Absent for rows not saved yet (all of
	 * them on create).
	 */
	id?: number
	place: DraftPlace
	dayCount: number
	/** False until the user touches the day stepper — the UI renders the default muted. */
	isTouched: boolean
}

let nextRowKey = 1

const newRowKey = (): string => `destination-${nextRowKey++}`

export interface CreateTripDraft {
	/** Ordered: array order is the visit order. */
	destinations: DraftDestination[]
	/** null → the trip uses {@link suggestedName}. */
	customName: string | null
	/** Dashed YYYY-MM-DD (the HTML date-input form), or null — undated is first-class. */
	startDate: string | null
	/** Lowercase ISO 4217 code. Always set — the selector shows the default, never a blank. */
	currencyCode: string
}

export const emptyDraft = (): CreateTripDraft => ({
	destinations: [],
	customName: null,
	startDate: null,
	currencyCode: DEFAULT_CURRENCY.code,
})

/** A saved destination, as the edit flow seeds it: its server id, place and days. */
export interface SeedDestination {
	id: number
	place: DraftPlace
	dayCount: number
}

/**
 * Seeds the edit flow with a trip's saved destinations (ids included) and
 * start date. The saved days are the user's own, so the rows start touched;
 * the currency is unused there and stays at the default.
 */
export const seedDraft = (
	destinations: SeedDestination[],
	startDate: string | null,
): CreateTripDraft => ({
	...emptyDraft(),
	destinations: destinations.map(({ id, place, dayCount }) => ({
		key: newRowKey(),
		id,
		place,
		dayCount,
		isTouched: true,
	})),
	startDate,
})

// MARK: Destinations

export const totalDays = (draft: CreateTripDraft): number =>
	draft.destinations.reduce((total, destination) => total + destination.dayCount, 0)

/** Zero-based trip day on which the destination at `index` begins. */
export const dayOffset = (draft: CreateTripDraft, index: number): number =>
	draft.destinations.slice(0, index).reduce((total, destination) => total + destination.dayCount, 0)

export const canAddDestination = (draft: CreateTripDraft): boolean =>
	draft.destinations.length < MAX_DESTINATIONS_PER_TRIP && totalDays(draft) < MAX_TRIP_DAY_COUNT

/**
 * Appends the picked place with the default day count, clamped to the days the
 * trip has left. Repeats are allowed — revisiting a place is a normal trip shape.
 * A place whose text the contract would reject (blank, or over the length
 * bound) is refused here, so a row can never carry a request the server 400s.
 */
export const addDestination = (draft: CreateTripDraft, place: DraftPlace): CreateTripDraft => {
	if (!canAddDestination(draft)) return draft
	if (placeTextProblem(place.location.fullText) !== null) return draft
	const dayCount = Math.min(DEFAULT_DESTINATION_DAYS, MAX_TRIP_DAY_COUNT - totalDays(draft))
	return {
		...draft,
		destinations: [...draft.destinations, { key: newRowKey(), place, dayCount, isTouched: false }],
	}
}

export const removeDestination = (draft: CreateTripDraft, index: number): CreateTripDraft => ({
	...draft,
	destinations: draft.destinations.filter((_, i) => i !== index),
})

export const moveDestination = (
	draft: CreateTripDraft,
	from: number,
	to: number,
): CreateTripDraft => {
	const destinations = [...draft.destinations]
	if (!(from in destinations) || !(to in destinations)) return draft
	const [moved] = destinations.splice(from, 1)
	destinations.splice(to, 0, moved)
	return { ...draft, destinations }
}

// MARK: Day counts

/** The most days the destination at `index` may hold without breaking either limit. */
const maxDaysAt = (draft: CreateTripDraft, index: number): number => {
	const others = totalDays(draft) - draft.destinations[index].dayCount
	return Math.min(MAX_DESTINATION_DAY_COUNT, MAX_TRIP_DAY_COUNT - others)
}

/** Sets the row's day count, clamped to [1, the contract limits]. Marks the row touched. */
export const setDays = (draft: CreateTripDraft, index: number, days: number): CreateTripDraft => {
	const destination = draft.destinations[index]
	if (!destination) return draft
	const dayCount = Math.max(1, Math.min(days, maxDaysAt(draft, index)))
	return {
		...draft,
		destinations: draft.destinations.map((d, i) =>
			i === index ? { ...d, dayCount, isTouched: true } : d,
		),
	}
}

export const incrementDays = (draft: CreateTripDraft, index: number): CreateTripDraft =>
	draft.destinations[index] ? setDays(draft, index, draft.destinations[index].dayCount + 1) : draft

export const decrementDays = (draft: CreateTripDraft, index: number): CreateTripDraft =>
	draft.destinations[index] ? setDays(draft, index, draft.destinations[index].dayCount - 1) : draft

// MARK: Name

/**
 * Auto-suggested trip name: the place, or the pair of places, and the year the
 * trip starts — "Toronto 2026", "Vietnam & Japan 2027". Past two, a list stops
 * reading as a name, so only the first place carries it. An undated trip has
 * no year to claim and goes without one. Mirrors the iOS draft.
 */
export const suggestedName = (draft: CreateTripDraft): string => {
	const names = draft.destinations.map(destination => destination.place.name)
	if (names.length === 0) return 'New trip'
	const places = names.length <= 2 ? names.join(' & ') : names[0]
	const start = parseCalendarDate(draft.startDate)
	return start ? `${places} ${start.year}` : places
}

/**
 * Records the typed name. Blank input — or typing the suggestion back — drops
 * to the suggestion, so the hint state is recoverable.
 */
export const setCustomName = (draft: CreateTripDraft, name: string): CreateTripDraft => {
	const trimmed = name.trim()
	return {
		...draft,
		customName: trimmed === '' || trimmed === suggestedName(draft) ? null : trimmed,
	}
}

export const isUsingCustomName = (draft: CreateTripDraft): boolean => draft.customName !== null

/** The name the trip will be created with: the custom name, or the suggestion. */
export const tripName = (draft: CreateTripDraft): string =>
	draft.customName ?? suggestedName(draft)

// MARK: Currency

/** The trip's default expense currency. */
export const setCurrencyCode = (draft: CreateTripDraft, code: string): CreateTripDraft => ({
	...draft,
	currencyCode: code,
})

// MARK: Start date

export const setStartDate = (draft: CreateTripDraft, date: string): CreateTripDraft => ({
	...draft,
	startDate: date,
})

export const clearStartDate = (draft: CreateTripDraft): CreateTripDraft => ({
	...draft,
	startDate: null,
})

/**
 * True when nothing worth keeping has been entered yet — closing the flow only
 * needs a discard confirmation when this is false.
 */
export const isDraftEmpty = (draft: CreateTripDraft): boolean =>
	draft.destinations.length === 0 &&
	draft.startDate === null &&
	draft.customName === null &&
	draft.currencyCode === DEFAULT_CURRENCY.code
